<script setup lang="ts">
/**
 * Eén component voor elke statusmelding. Vervangt Components/VMessage (severity),
 * VNotice (type), VAlertBanner (vrije bannerClass) en VToast (type): vier componenten
 * met elk een ander vocabularium (`warn` vs `warning`), andere kleuren en
 * patchy dark mode, voor hetzelfde idee.
 *
 * - `severity`: wat voor melding. Bepaalt kleur en icoon.
 * - `variant`: waar ze staat.
 *   - `inline` (standaard): blok in de pagina of een formulier.
 *   - `banner`: balk over de volle breedte, bovenaan een layout (impersonatie,
 *     proefperiode, koppelingsfouten).
 *   - `toast`: zwevende melding, via useToast/VToastContainer.
 */
import {
    IconAlertCircle,
    IconAlertTriangle,
    IconCircleCheck,
    IconInfoCircle,
    IconX,
} from '@tabler/icons-vue'
import { computed, ref, useSlots, watch, type Component } from 'vue'

export type MessageSeverity = 'success' | 'info' | 'warning' | 'error' | 'neutral'
export type MessageVariant = 'inline' | 'banner' | 'toast'

const props = withDefaults(
    defineProps<{
        severity?: MessageSeverity
        variant?: MessageVariant
        /** Vetgedrukte titel boven (inline) of vóór (banner/toast) de tekst. */
        title?: string | null
        /** Eenvoudige tekst; voor opmaak de default-slot gebruiken. */
        message?: string | null
        closable?: boolean
        /** Met v-model:visible te sluiten/tonen door de ouder. */
        visible?: boolean
        /** Icoon per severity tonen. */
        icon?: boolean
    }>(),
    {
        severity: 'info',
        variant: 'inline',
        title: null,
        message: null,
        closable: false,
        visible: true,
        icon: true,
    }
)

const emit = defineEmits<{
    'update:visible': [value: boolean]
    close: []
}>()

const slots = useSlots()

// Zelf verbergen na een klik op het kruisje. De oude VMessage emitte enkel
// update:visible; zonder v-model bij de ouder (overal het geval) deed het kruisje niets.
const dismissed = ref(false)

watch(
    () => props.visible,
    (visible) => {
        if (visible) {
            dismissed.value = false
        }
    }
)

function close(): void {
    dismissed.value = true
    emit('update:visible', false)
    emit('close')
}

const ICONS: Record<MessageSeverity, Component> = {
    success: IconCircleCheck,
    info: IconInfoCircle,
    warning: IconAlertTriangle,
    error: IconAlertCircle,
    neutral: IconInfoCircle,
}

/**
 * Kleuren: bewust die van de oude componenten (keuze klant, opkuis stap 3), zodat
 * bestaande schermen dezelfde kleuren houden. Inline = oude VMessage (neutraal = oude
 * VNotice "default"), banner = de bannerClasses die de layouts gebruikten, toast = oude
 * VToast: dezelfde doorschijnende tint, maar enkel de achtergrond (vroeger opacity-75 op
 * het hele element, waardoor ook tekst en kruisje vervaagd waren). Dark-mode-varianten zijn nieuw.
 */
const INLINE: Record<MessageSeverity, { box: string; icon: string }> = {
    success: {
        box: 'bg-green-100 border-green-400 text-green-800 dark:bg-green-900/40 dark:border-green-700 dark:text-green-100',
        icon: 'text-green-500',
    },
    info: {
        box: 'bg-blue-100 border-blue-400 text-blue-800 dark:bg-blue-900/40 dark:border-blue-700 dark:text-blue-100',
        icon: 'text-blue-500',
    },
    warning: {
        box: 'bg-yellow-100 border-yellow-400 text-yellow-800 dark:bg-yellow-900/40 dark:border-yellow-700 dark:text-yellow-100',
        icon: 'text-yellow-500',
    },
    error: {
        box: 'bg-red-100 border-red-400 text-red-800 dark:bg-red-900/40 dark:border-red-700 dark:text-red-100',
        icon: 'text-red-500',
    },
    neutral: {
        box: 'bg-gray-200 border-gray-300 text-gray-800 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100',
        icon: 'text-gray-500',
    },
}

const BANNER: Record<MessageSeverity, { box: string; icon: string }> = {
    success: {
        box: 'bg-green-100 border-green-200 text-gray-900 dark:bg-green-900 dark:text-green-100',
        icon: 'text-green-600',
    },
    info: {
        box: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950 dark:text-blue-100',
        icon: 'text-blue-600',
    },
    warning: {
        box: 'bg-warning-banner border-gray-200 text-gray-900 dark:bg-yellow-900 dark:text-yellow-100',
        icon: 'text-amber-600',
    },
    error: {
        box: 'bg-red-100 border-gray-200 text-gray-900 dark:bg-red-900 dark:text-red-100',
        icon: 'text-red-600',
    },
    neutral: {
        box: 'bg-yellow-50 border-gray-200 text-gray-900 dark:bg-gray-800 dark:text-gray-100',
        icon: 'text-gray-500',
    },
}

const TOAST: Record<MessageSeverity, string> = {
    success: 'bg-lime-600/75 text-white',
    info: 'bg-blue-600/75 text-white',
    warning: 'bg-yellow-400/75 text-white',
    error: 'bg-red-700/75 text-white',
    neutral: 'bg-gray-600/75 text-white',
}

const iconComponent = computed(() => ICONS[props.severity])

const containerClasses = computed<string[]>(() => {
    switch (props.variant) {
        case 'banner':
            return ['w-full border-b px-4 py-3', BANNER[props.severity].box]
        case 'toast':
            return ['rounded-md px-4 py-3 shadow-lg', TOAST[props.severity]]
        default:
            return ['rounded-lg border p-4', INLINE[props.severity].box]
    }
})

const iconClasses = computed<string>(() => {
    if (props.variant === 'toast') {
        return 'text-white'
    }
    return (props.variant === 'banner' ? BANNER : INLINE)[props.severity].icon
})

/** Screenreaders: fouten en waarschuwingen onderbreken, de rest wacht beleefd. */
const role = computed(() =>
    props.severity === 'error' || props.severity === 'warning' ? 'alert' : 'status'
)

const hasBody = computed(() => !!slots.default || !!props.message)
</script>

<template>
    <Transition name="v-message-transition">
        <div
            v-if="visible && !dismissed"
            :class="['v-message flex items-start gap-3 text-sm', containerClasses]"
            :role="role"
            :data-severity="severity"
            :data-variant="variant"
        >
            <!-- In een banner staat het icoon mee gecentreerd naast de tekst. -->
            <component
                v-if="icon && variant !== 'banner'"
                :is="iconComponent"
                :class="['w-5 h-5 flex-shrink-0 mt-px', iconClasses]"
                aria-hidden="true"
            />

            <div
                :class="[
                    'flex-1 min-w-0',
                    variant === 'banner'
                        ? 'flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center'
                        : 'flex flex-col gap-1',
                ]"
            >
                <component
                    v-if="icon && variant === 'banner'"
                    :is="iconComponent"
                    :class="['w-5 h-5 flex-shrink-0', iconClasses]"
                    aria-hidden="true"
                />
                <!-- div, geen p: een globale p-stijl zou de kleur van de melding overschrijven. -->
                <div v-if="title || slots.title" class="font-semibold">
                    <slot name="title">{{ title }}</slot>
                </div>
                <div v-if="hasBody" class="font-medium">
                    <slot>{{ message }}</slot>
                </div>
                <div v-if="slots.actions" class="flex flex-wrap items-center gap-2">
                    <slot name="actions"></slot>
                </div>
            </div>

            <button
                v-if="closable"
                type="button"
                class="flex-shrink-0 rounded p-0.5 opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-current"
                :aria-label="$t('global.close')"
                @click="close"
            >
                <IconX class="w-4 h-4" aria-hidden="true" />
            </button>
        </div>
    </Transition>
</template>

<style scoped>
.v-message-transition-enter-from,
.v-message-transition-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}

.v-message-transition-enter-active,
.v-message-transition-leave-active {
    transition: all 0.2s ease-in-out;
}
</style>
